import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { tenantsService } from '../services/tenants.service';
import {
  AgencyRoom,
  getTenantStatusLabel,
  statutDuBail,
} from '../types/tenant';
import { getErrorMessage } from '../utils/error';
import { formatDate } from '../utils/format';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';

/**
 * Espace agence — LISTE des lots où déposer une pièce.
 *
 * Vue orientée lots : l'agence choisit une chambre. Elle NE voit QUE le numéro,
 * le nom du lot, l'identité de l'occupant et les dates de bail — ni loyer, ni
 * charges, ni contact, ni les documents déjà déposés. Elle dépose, elle ne
 * consulte pas.
 *
 * **Le formulaire est une PAGE, plus un repli.** Les lignes dépliables ont coûté
 * quatre défauts sur l'écran Agences — contenu écrasé dans une cellule, boutons
 * qui débordent, libellés qui basculent, table imbriquée. Le même motif était ici.
 */
/** Le statut du bail d'un lot. Passe par la règle PARTAGÉE avec l'écran du
 *  propriétaire : deux comparaisons de dates écrites séparément finiraient par
 *  dire deux choses différentes. */
const statutDuLot = (room: AgencyRoom) =>
  statutDuBail(room.noticeDate, room.leaseStartDate);

const AgencyTenantDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  const [rooms, setRooms] = useState<AgencyRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRooms(await tenantsService.listAgencyRooms());
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement des chambres'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Le backend borne déjà aux colocations partagées ; ce filtre suit simplement
  // le sélecteur de colocation de la barre latérale.
  const visibleRooms = selectedColoc
    ? rooms.filter((r) => r.colocId === selectedColoc.id)
    : rooms;

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <h2>Déposer un document</h2>
      </div>

      <p className="access-hint">
        Choisissez le lot concerné. Vous déposez une pièce dans le dossier de son
        occupant ; vous ne voyez pas ce qu'il contient déjà.
      </p>

      {error && <div className="candidats-error">{error}</div>}

      {visibleRooms.length === 0 ? (
        <div className="socle-vide">
          <p className="socle-vide-titre">Aucune chambre partagée</p>
          <p className="socle-vide-aide">
            Le propriétaire ne vous a pas encore donné accès à une colocation, ou
            l'accès a été suspendu.
          </p>
        </div>
      ) : (
        <div className="contacts-table-container">
          <table className="candidats-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Lot</th>
                <th>Chambre</th>
                <th>Occupant</th>
                <th>Statut</th>
                <th className="colonne-manques">Il manque</th>
                <th>Entrée</th>
                <th>Préavis</th>
                <th>Sortie prévue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRooms.map((room) => {
                const occupant = room.tenantInfoId
                  ? `${room.firstName || ''} ${room.lastName || ''}`.trim()
                  : null;
                return (
                  <tr
                    key={room.roomId}
                    className={room.tenantInfoId ? 'candidats-row' : undefined}
                    onClick={() =>
                      room.tenantInfoId && navigate(`/agency/documents/${room.roomId}`)
                    }
                  >
                    <td>{room.roomNumber ?? '—'}</td>
                    <td>{room.lotNumber || '—'}</td>
                    <td className="candidat-name">{room.roomName}</td>
                    <td>{occupant || 'Libre'}</td>
                    {/* Le STATUT, avec le même vocabulaire que chez le
                        propriétaire : l'agence voyait un nom sans savoir si la
                        personne avait emménagé. Aucune donnée nouvelle — préavis
                        et date de début figurent déjà dans ses colonnes. */}
                    <td>
                      {!room.tenantInfoId ? (
                        <span className="access-hint">—</span>
                      ) : (
                        <span className={`tenant-status-badge status-${statutDuLot(room)}`}>
                          {getTenantStatusLabel(statutDuLot(room))}
                        </span>
                      )}
                    </td>
                    {/* Ce qui MANQUE, jamais ce qu'il y a : une liste de
                        courses, pas une divulgation. Rien du bail ni des pièces
                        de candidature ne transparaît. */}
                    <td className="colonne-manques">
                      {!room.dossierSuivi ? (
                        <span className="access-hint">—</span>
                      ) : room.dossierManques.length === 0 ? (
                        <span className="access-hint">Rien, dossier complet</span>
                      ) : (
                        room.dossierManques.map((m, i) => (
                          <span key={m} style={{ display: 'block' }}>
                            {m}
                            {i < room.dossierManques.length - 1 ? '' : ''}
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      {room.leaseStartDate ? formatDate(room.leaseStartDate) : '—'}
                    </td>
                    <td>
                      {room.noticeDate ? formatDate(room.noticeDate) : '—'}
                    </td>
                    <td>
                      {room.noticeExpectedExitDate
                        ? formatDate(room.noticeExpectedExitDate)
                        : '—'}
                    </td>
                    <td className="cx-actions">
                      {/* Un lot libre n'a pas de dossier où déposer : le dire
                          plutôt que d'offrir un bouton qui ne fera rien. */}
                      {room.tenantInfoId ? (
                        <button
                          className="btn-action btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/agency/documents/${room.roomId}`);
                          }}
                        >
                          Déposer
                        </button>
                      ) : (
                        <span className="access-hint">Lot libre</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgencyTenantDocumentsPage;
